import { useState } from "react";
import {
	useGetFilteredAssessmentsQuery,
	useGetGradesByStudentQuery,
	useCreateAssessmentGradeMutation,
	useUpdateAssessmentGradeMutation,
} from "../../../store/api";

export const useAssessmentModal = () => {
	const [showAssessmentModal, setShowAssessmentModal] = useState(false);
	const [selectedGradeData, setSelectedGradeData] = useState(null);
	const [isSaving, setIsSaving] = useState(false);

	// Create and update mutations
	const [createGrade] = useCreateAssessmentGradeMutation();
	const [updateGrade] = useUpdateAssessmentGradeMutation();

	const getCurrentSchoolYear = () => {
		const currentDate = new Date();
		const currentYear = currentDate.getFullYear();
		const year = currentDate.getMonth() >= 8 ? currentYear : currentYear - 1;
		return `${year}-09-01`;
	};

	// Fetch assessments when modal data is set
	const { data: assessments = [], isLoading: isLoadingAssessments } =
		useGetFilteredAssessmentsQuery(
			{
				teacherId: undefined, // Let it get all assessments for the subject
				subjectId: selectedGradeData?.subjectId,
				departmentId: selectedGradeData?.departmentId,
				schoolYearStart: getCurrentSchoolYear(),
			},
			{
				skip: !selectedGradeData?.subjectId || !selectedGradeData?.departmentId,
			}
		);

	// Fetch student grades with refetch function
	const {
		data: studentGradesResponse,
		isLoading: isLoadingGrades,
		refetch: refetchStudentGrades,
	} = useGetGradesByStudentQuery(selectedGradeData?.studentId, {
		skip: !selectedGradeData?.studentId,
	});

	// Filter assessments for the selected month
	const monthAssessments = assessments.filter((assessment) => {
		const assessmentDate = new Date(assessment.date);
		const assessmentMonth = assessmentDate.toLocaleString("default", {
			month: "long",
		});
		return assessmentMonth === selectedGradeData?.monthName;
	});

	// Process the student grades response correctly
	const studentGrades = (() => {
		if (!studentGradesResponse?.subjects || !selectedGradeData?.subjectId) {
			return {};
		}

		// Find the subject that matches our selected subject
		const subjectData = studentGradesResponse.subjects.find(
			(subject) => subject.subjectId === selectedGradeData.subjectId
		);

		if (!subjectData?.assessments) {
			return {};
		}

		// Create lookup object with correct property names
		const gradesLookup = {};
		subjectData.assessments.forEach((assessment) => {
			if (assessment.gradeId) {
				gradesLookup[assessment.assessmentId] = {
					id: assessment.gradeId,
					grade: assessment.grade,
					subjectAssessmentId: assessment.assessmentId,
					studentId: selectedGradeData.studentId,
					date: assessment.assessmentDate,
				};
			}
		});

		return gradesLookup;
	})();

	const handleGradeCellClick = (
		studentId,
		studentName,
		subjectId,
		subjectName,
		month,
		monthName,
		departmentId
	) => {
		setSelectedGradeData({
			studentId,
			studentName,
			subjectId,
			subjectName,
			month,
			monthName,
			departmentId,
		});
		setShowAssessmentModal(true);
	};

	const closeAssessmentModal = () => {
		setShowAssessmentModal(false);
		setSelectedGradeData(null);
	};

	const handleSaveGrades = async (gradesToSave) => {
		setIsSaving(true);
		try {
			// Process each grade
			for (const gradeData of gradesToSave) {
				const existingGrade = studentGrades[gradeData.assessmentId];

				if (existingGrade) {
					// Update existing grade
					await updateGrade({
						id: existingGrade.id,
						studentId: gradeData.studentId,
						subjectAssessmentId: gradeData.assessmentId,
						grade: gradeData.points?.toString() || "0",
						date: new Date().toISOString(),
					}).unwrap();
				} else if (gradeData.points !== null && gradeData.points !== "") {
					// Create new grade (only if points are provided)
					await createGrade({
						studentId: gradeData.studentId,
						subjectAssessmentId: gradeData.assessmentId,
						grade: gradeData.points?.toString() || "0",
						date: new Date().toISOString(),
					}).unwrap();
				}
			}

			if (selectedGradeData) {
				console.log("Grades saved successfully");
			}

			// Close modal on success
			closeAssessmentModal();
		} catch (error) {
			console.error("Failed to save grades:", error);
		} finally {
			setIsSaving(false);
		}
	};

	return {
		showAssessmentModal,
		selectedGradeData,
		monthAssessments,
		studentGrades,
		isLoading: isLoadingAssessments || isLoadingGrades,
		isSaving,
		handleGradeCellClick,
		closeAssessmentModal,
		handleSaveGrades,
	};
};
